import { useCallback, useEffect, useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { CommonDatagrid } from '..';
import { DriverRatingsService } from '../../../api/services/driverRatingsService';
import {
  setDrivers,
  setPagination,
  setSelectedDriver,
} from '../../../store/Reducers/DriverRatingsSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { IDriverRatingSummary } from '../../../types/api/response/driverratingsresponse';
import { IRootState } from '../../../types/store';
import { driverSourceLabel, getDriverRatingsColumns } from './columns';
import { DriverRatingsDetailDialog } from './DriverRatingsDetailDialog';

const SOURCES = ['LOCUS', 'SUPERPRO', 'UNKNOWN'];

const toParam = (d: Dayjs | null) => (d && d.isValid() ? d.format('YYYY-MM-DD') : undefined);

export const DriverRatingsDatagrid = () => {
  const dispatch = useDispatch();
  const { drivers, totalElements, pagination, selectedDriver } = useSelector(
    (state: IRootState) => state.driverRatings,
  );

  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  // The range the grid's totals were last fetched with — the drill-down uses the same one.
  const [appliedRange, setAppliedRange] = useState<{ from?: string; to?: string }>({});
  const [detailOpen, setDetailOpen] = useState(false);

  const dateError =
    fromDate && toDate && toDate.isBefore(fromDate, 'day')
      ? '"To" must be on or after "From".'
      : '';

  const columns = getDriverRatingsColumns();

  const fetchDrivers = useCallback(
    async (
      page = 0,
      size = pagination.size,
      sourceVal = source,
      searchVal = search,
      from = toParam(fromDate),
      to = toParam(toDate),
    ) => {
      try {
        const res = await DriverRatingsService.searchDriverRatings({
          ...(sourceVal ? { source: sourceVal } : {}),
          ...(searchVal ? { search: searchVal } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          page,
          size,
        });
        const content = res.content.map((d) => ({
          ...d,
          row_key: `${d.driver_source}:${d.driver_id ?? ''}`,
        }));
        dispatch(setDrivers({ content, totalElements: res.page.totalElements }));
        dispatch(setPagination({ page: res.page.number, size: res.page.size }));
        setAppliedRange({ from, to });
      } catch {
        dispatch(
          showNotification({ message: 'Failed to fetch driver ratings.', severity: 'error' }),
        );
      }
    },
    [dispatch, pagination.size, source, search, fromDate, toDate],
  );

  useEffect(() => {
    fetchDrivers(0, pagination.size, '', '', undefined, undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Unattributed row has no driver id, so nothing to drill into (TRD §6.1).
  const openDetail = (driver: IDriverRatingSummary) => {
    if (!driver.driver_id || driver.driver_source === 'UNKNOWN') return;
    dispatch(setSelectedDriver(driver));
    setDetailOpen(true);
  };

  const filterBar = (
    <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end" mb={2}>
      <TextField
        select
        label="Fleet"
        size="small"
        value={source}
        onChange={(e) => setSource(e.target.value)}
        sx={{ minWidth: 180 }}
      >
        <MenuItem value="">All fleets</MenuItem>
        {SOURCES.map((s) => (
          <MenuItem key={s} value={s}>
            {driverSourceLabel(s)}
          </MenuItem>
        ))}
      </TextField>
      <DatePicker
        label="From"
        value={fromDate}
        onChange={setFromDate}
        slotProps={{ textField: { size: 'small', sx: { width: 175 } } }}
      />
      <DatePicker
        label="To"
        value={toDate}
        onChange={setToDate}
        slotProps={{ textField: { size: 'small', sx: { width: 175 } } }}
      />
      <TextField
        size="small"
        label="Driver"
        placeholder="Search by driver id or name..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 240 }}
      />
      <Button
        variant="contained"
        onClick={() => fetchDrivers(0, pagination.size)}
        disabled={!!dateError}
        sx={{ fontSize: 12, fontWeight: 600, height: 40 }}
      >
        Apply Filters
      </Button>
      <Button
        variant="outlined"
        onClick={() => {
          setSource('');
          setSearch('');
          setFromDate(null);
          setToDate(null);
          fetchDrivers(0, pagination.size, '', '', undefined, undefined);
        }}
        sx={{ fontSize: 12, height: 40 }}
      >
        Clear
      </Button>
      {dateError && (
        <Typography variant="caption" color="error" sx={{ width: '100%', mt: -1 }}>
          {dateError}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mt: -1 }}>
        Averages and counts cover the selected dates. Lowest average first. Ratings with no known
        driver, including those from before driver tracking began, are grouped as Unattributed.
      </Typography>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CommonDatagrid
        header={filterBar}
        data={drivers}
        columns={columns}
        rowId="row_key"
        serverPagination
        page={pagination.page}
        pageSize={pagination.size}
        rowCount={totalElements}
        onPageChange={(p) =>
          fetchDrivers(p, pagination.size, source, search, appliedRange.from, appliedRange.to)
        }
        onPageSizeChange={(s) =>
          fetchDrivers(0, s, source, search, appliedRange.from, appliedRange.to)
        }
        onRowClick={({ row }) => openDetail(row as IDriverRatingSummary)}
        isCellEditable={() => false}
      />
      <DriverRatingsDetailDialog
        driver={selectedDriver}
        from={appliedRange.from}
        to={appliedRange.to}
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          dispatch(setSelectedDriver(null));
        }}
      />
    </LocalizationProvider>
  );
};
